/**
 * DepthSelector
 * Derinlik seçimi listesi - Entitlement tabanlı
 */

import React from 'react';
import { Sparkles, Lock, Shield, AlertCircle } from 'lucide-react';

export default function DepthSelector({ 
  depths, 
  selected, 
  onSelect, 
  userCredits,
  entitlement = null,  // Backend entitlement response
  isLoadingEntitlement = false,
  entitlementError = null
}) {
  // Entitlement loading state
  if (isLoadingEntitlement) {
    return (
      <div className="oracle-depth-list">
        <div style={{ 
          textAlign: 'center', 
          padding: '24px', 
          color: 'var(--oracle-text-muted)' 
        }}>
          Paket erişimi kontrol ediliyor...
        </div>
      </div>
    );
  }
  
  // Entitlement error state
  if (entitlementError) {
    return (
      <div className="oracle-depth-list">
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
          color: '#ef4444'
        }}>
          <AlertCircle size={24} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            Oracle servisine şu anda bağlanılamıyor.
          </div>
          <div style={{ fontSize: 12, color: 'var(--oracle-text-muted)' }}>
            Lütfen birkaç saniye sonra tekrar deneyin.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="oracle-depth-list">
      {/* Admin Test Mode Banner */}
      {entitlement?.admin_test_mode && (
        <div style={{ 
          background: 'rgba(139, 92, 246, 0.15)', 
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: 12,
          padding: 12,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <Shield size={20} color="#a78bfa" />
          <div>
            <div style={{ fontSize: 14, color: '#a78bfa', fontWeight: 600 }}>
              Admin Test Modu
            </div>
            <div style={{ fontSize: 12, color: 'var(--oracle-text-muted)' }}>
              Tüm paketlere ücretsiz erişim
            </div>
          </div>
        </div>
      )}
      
      {depths.map(depth => {
        // Entitlement'tan erişim bilgisini al
        const packageEntitlement = entitlement?.packages?.find(p => p.id === depth.id);
        
        // Eğer entitlement yoksa, kredi kontrolü yap (fallback)
        const canAfford = packageEntitlement 
          ? packageEntitlement.eligible 
          : userCredits >= depth.credits;
        
        const accessReason = packageEntitlement?.access_reason || 'unknown';
        const isAdminAccess = accessReason === 'admin_test_mode';
        
        return (
          <div
            key={depth.id}
            className={`oracle-glass-card oracle-depth-card ${selected === depth.id ? 'selected' : ''}`}
            onClick={() => canAfford && onSelect(depth.id)}
            style={{ 
              opacity: canAfford ? 1 : 0.5, 
              cursor: canAfford ? 'pointer' : 'not-allowed',
              position: 'relative'
            }}
            data-testid={`depth-${depth.id}`}
          >
            {/* Lock icon for disabled packages */}
            {!canAfford && (
              <div style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
                padding: 6
              }}>
                <Lock size={14} color="#9ca3af" />
              </div>
            )}
            
            {/* Admin badge */}
            {isAdminAccess && (
              <div style={{
                position: 'absolute',
                top: -8,
                right: 12,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                borderRadius: 12,
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 600,
                color: 'white'
              }}>
                TEST
              </div>
            )}
            
            <div className="oracle-depth-info">
              <div className="oracle-depth-name">{depth.name}</div>
              <div className="oracle-depth-desc">{depth.description}</div>
            </div>
            <div className="oracle-depth-price">
              <div className="oracle-depth-credits" style={{ 
                textDecoration: isAdminAccess ? 'line-through' : 'none',
                opacity: isAdminAccess ? 0.5 : 1
              }}>
                <Sparkles size={16} style={{ display: 'inline', marginRight: 4 }} />
                {depth.credits}
              </div>
              <div className="oracle-depth-unit">
                {isAdminAccess ? 'ücretsiz' : 'kredi'}
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Insufficient credits message */}
      {!entitlement?.admin_test_mode && userCredits < 5 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '12px', 
          color: 'var(--oracle-text-muted)',
          fontSize: 13
        }}>
          Yetersiz kredi. Kredi satın almak için ana menüye dönün.
        </div>
      )}
    </div>
  );
}
